import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Tag } from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const groupOptions = ['Freight', 'Documentation', 'Customs', 'Handling', 'Storage', 'Other'];

const RevenueType = () => {
  const getSessionData = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  };

  const session   = getSessionData();
  const companyId = session?.companyid;

  const [revenueTypes,         setRevenueTypes]         = useState([]);
  const [filteredRevenueTypes, setFilteredRevenueTypes] = useState([]);
  const [acHeads,              setAcHeads]              = useState([]);
  const [acHeadsLoading,       setAcHeadsLoading]       = useState(false);
  const [loading,              setLoading]              = useState(true);
  const [searchTerm,           setSearchTerm]           = useState('');
  const [filterGroup,          setFilterGroup]          = useState('');
  const [filterStatus,         setFilterStatus]         = useState('all');
  const [showModal,            setShowModal]            = useState(false);
  const [editMode,             setEditMode]             = useState(false);
  const [submitLoading,        setSubmitLoading]        = useState(false);
  const [errors,               setErrors]               = useState({});

  const emptyForm = { revenuetypeid: null, revenuetypename: '', revenuetypegroup: '', accountheadid: '', costaccountheadid: '', isactive: true };
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }
    fetchRevenueTypes(); fetchAcHeads();
  }, [companyId]);

  useEffect(() => { filterItems(); }, [searchTerm, filterGroup, filterStatus, revenueTypes]);

  const fetchRevenueTypes = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/RevenueType.php');
      if (res.data.success) setRevenueTypes(res.data.data || []);
      else alert('Failed to fetch revenue types: ' + res.data.message);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error fetching revenue types.');
    } finally { setLoading(false); }
  };

  const fetchAcHeads = async () => {
    setAcHeadsLoading(true);
    try {
      const res = await apiClient.get('/AcHeads.php', { params: { statushide: 0 } });
      if (res.data.success) setAcHeads(res.data.data || []);
    } catch (e) { console.error(e); }
    finally { setAcHeadsLoading(false); }
  };

  const filterItems = () => {
    let f = revenueTypes;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      f = f.filter(rt => rt.revenuetypename.toLowerCase().includes(q) || rt.revenuetypecode.toLowerCase().includes(q) || rt.revenuetypegroup?.toLowerCase().includes(q));
    }
    if (filterGroup)         f = f.filter(rt => rt.revenuetypegroup === filterGroup);
    if (filterStatus !== 'all') f = f.filter(rt => rt.isactive === (filterStatus === 'active' ? 1 : 0));
    setFilteredRevenueTypes(f);
  };

  const presentGroups = [...new Set(revenueTypes.map(rt => rt.revenuetypegroup).filter(Boolean))].sort();

  const validateForm = () => {
    const errs = {};
    if (!formData.revenuetypename.trim()) errs.revenuetypename   = 'Revenue type name is required';
    if (!formData.accountheadid)          errs.accountheadid     = 'Revenue Account Head is required';
    if (!formData.costaccountheadid)      errs.costaccountheadid = 'Cost Account Head is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const payload = { ...formData, accountheadid: formData.accountheadid ? parseInt(formData.accountheadid) : null, costaccountheadid: formData.costaccountheadid ? parseInt(formData.costaccountheadid) : null, isactive: formData.isactive ? 1 : 0 };
      const res = await apiClient[editMode ? 'put' : 'post']('/RevenueType.php', payload);
      if (res.data.success) {
        alert(editMode ? 'Revenue type updated successfully!' : 'Revenue type created successfully!');
        closeModal(); fetchRevenueTypes();
      } else { alert('Error: ' + res.data.message); }
    } catch (err) {
      if (err.response?.status === 401)      alert('Session expired. Please login again.');
      else if (err.response?.status === 409) alert('Could not generate a unique code. Please try a different name.');
      else alert('Error saving revenue type: ' + (err.response?.data?.message || err.message));
    } finally { setSubmitLoading(false); }
  };

  const handleEdit = (rt) => {
    setFormData({ revenuetypeid: rt.revenuetypeid, revenuetypename: rt.revenuetypename, revenuetypegroup: rt.revenuetypegroup || '', accountheadid: rt.accountheadid || '', costaccountheadid: rt.costaccountheadid || '', isactive: rt.isactive === 1 });
    setEditMode(true); setShowModal(true); setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this revenue type?')) return;
    try {
      const res = await apiClient.delete('/RevenueType.php', { data: { revenuetypeid: id } });
      if (res.data.success) { alert('Revenue type deleted successfully!'); fetchRevenueTypes(); }
      else alert('Error: ' + res.data.message);
    } catch (err) { alert('Error deleting revenue type: ' + (err.response?.data?.message || 'Please try again.')); }
  };

  const resetForm    = () => { setFormData(emptyForm); setEditMode(false); setErrors({}); };
  const openAddModal = () => { resetForm(); setShowModal(true); };
  const closeModal   = () => { setShowModal(false); resetForm(); };
  const clearFilters = () => { setSearchTerm(''); setFilterGroup(''); setFilterStatus('all'); };

  const inputCls  = (err) => `w-full px-3.5 py-3 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;
  const selectCls = (err, disabled) => `w-full px-3.5 py-3 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] ${disabled ? 'opacity-60 cursor-not-allowed bg-gray-50' : ''} ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;
  const filterSelectCls = `px-3.5 py-2.5 border-2 border-gray-200 rounded-lg text-sm font-semibold text-gray-700 bg-white outline-none cursor-pointer transition-all duration-150 hover:border-[#0d1c6b] focus:border-[#0d1c6b]`;
  const labelCls  = 'block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide';
  const errMsg    = (msg) => msg ? <p className="text-xs text-red-600 font-semibold mt-1">⚠ {msg}</p> : null;

  // ── no session ─────────────────────────────────────────────────────────
  if (!companyId) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center p-10 bg-white rounded-xl shadow-sm border border-gray-200">
          <Tag size={40} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">No Session Context</h3>
          <p className="text-gray-500 text-sm">Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  const hasFilters = searchTerm || filterGroup || filterStatus !== 'all';

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Tag size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>Revenue Type Master</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>Manage revenue types and their account mappings</p>
          </div>
        </div>
        <button onClick={openAddModal}
          className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50"
          style={{ color: NAVY }}>
          <Plus size={17} /> Add Revenue Type
        </button>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
          focus-within:border-[#0d1c6b] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input type="text" placeholder="Search by code, name, or group..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium placeholder:text-gray-400 placeholder:font-normal" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>

        <select value={filterGroup} onChange={e => setFilterGroup(e.target.value)} className={filterSelectCls}>
          <option value="">All Groups</option>
          {presentGroups.map(g => <option key={g} value={g}>{g}</option>)}
        </select>

        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={filterSelectCls}>
          <option value="all">All Status</option>
          <option value="active">Active Only</option>
          <option value="inactive">Inactive Only</option>
        </select>

        {hasFilters && (
          <button onClick={clearFilters}
            className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-150 hover:bg-gray-50 hover:border-gray-300">
            <X size={14} /> Clear
          </button>
        )}

        <div className="text-sm font-semibold text-gray-500 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg whitespace-nowrap">
          Total: {filteredRevenueTypes.length} {filteredRevenueTypes.length === 1 ? 'revenue type' : 'revenue types'}
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="flex-1 p-5">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-lg font-semibold">Loading revenue types...</div>
        ) : filteredRevenueTypes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
            <Tag size={40} className="text-gray-300 mb-4" />
            <p className="text-xl text-gray-400 font-semibold mb-3">No revenue types found</p>
            {hasFilters && (
              <button onClick={clearFilters}
                className="text-sm text-blue-600 underline cursor-pointer bg-transparent border-0">Clear filters</button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full border-collapse" style={{ minWidth: 900 }}>
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                  {['Code', 'Revenue Type', 'Group', 'Revenue A/C', 'Cost A/C', 'Status', 'Created At', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0"
                      style={{ color: '#fff', background: 'inherit' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRevenueTypes.map(rt => (
                  <tr key={rt.revenuetypeid}
                    className="border-b border-gray-100 transition-all duration-150 hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b]">
                    <td className="px-4 py-3.5">
                      <span className="font-mono text-[12px] font-bold text-violet-700 bg-violet-50 px-2 py-0.5 rounded">
                        {rt.revenuetypecode}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 font-semibold text-gray-800">{rt.revenuetypename}</td>
                    <td className="px-4 py-3.5">
                      {rt.revenuetypegroup ? (
                        <span className="inline-block px-2.5 py-1 bg-blue-50 text-blue-700 border-2 border-blue-200 rounded-lg text-[12px] font-bold">
                          {rt.revenuetypegroup}
                        </span>
                      ) : <span className="text-gray-400 italic">—</span>}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-700" title={rt.accountheadcode}>
                      {rt.accountheadname || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-700" title={rt.costaccountheadcode}>
                      {rt.costaccountheadname || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide border-2 ${
                        rt.isactive ? 'bg-emerald-50 text-emerald-700 border-emerald-400' : 'bg-red-50 text-red-700 border-red-400'
                      }`}>
                        {rt.isactive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-500">
                      {rt.createdat ? new Date(rt.createdat).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3.5 sticky right-0 bg-white shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.08)]">
                      <div className="flex gap-1.5">
                        <button onClick={() => handleEdit(rt)} title="Edit"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150 hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(109,40,217,0.3)]">
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => handleDelete(rt.revenuetypeid)} title="Delete"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-red-100 border-red-400 text-red-600 transition-all duration-150 hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(220,38,38,0.3)]">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          style={{ animation: 'fadeIn 0.2s ease' }}>
          <div className="bg-white rounded-2xl w-full max-w-[640px] shadow-2xl border border-gray-200 overflow-hidden"
            style={{ animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}>

            <div className="flex items-center justify-between px-7 py-5"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide m-0" style={{ color: '#fff' }}>
                <Tag size={18} /> {editMode ? 'Edit Revenue Type' : 'Add New Revenue Type'}
              </h3>
              <button onClick={closeModal}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-7">

              {/* Name + Group */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelCls}>Revenue Type Name <span className="text-red-500">*</span></label>
                  <input type="text" value={formData.revenuetypename}
                    onChange={e => setFormData({ ...formData, revenuetypename: e.target.value })}
                    maxLength="200" placeholder="e.g., Freight Charges, Documentation Fee"
                    className={inputCls(errors.revenuetypename)} autoFocus />
                  {errMsg(errors.revenuetypename)}
                  {!editMode && <p className="text-[11px] text-gray-400 mt-1">Code will be auto-generated from this name</p>}
                </div>
                <div>
                  <label className={labelCls}>Group</label>
                  <select value={formData.revenuetypegroup} onChange={e => setFormData({ ...formData, revenuetypegroup: e.target.value })}
                    className={selectCls(false, false)}>
                    <option value="">— No Group —</option>
                    {groupOptions.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                  <p className="text-[11px] text-gray-400 mt-1">Optional category for grouping revenue types</p>
                </div>
              </div>

              {/* Revenue A/C + Cost A/C */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelCls}>Revenue Account Head <span className="text-red-500">*</span></label>
                  <select value={formData.accountheadid || ''} disabled={acHeadsLoading}
                    onChange={e => setFormData({ ...formData, accountheadid: e.target.value ? parseInt(e.target.value) : '' })}
                    className={selectCls(errors.accountheadid, acHeadsLoading)}>
                    <option value="">{acHeadsLoading ? 'Loading...' : '— Select Account Head —'}</option>
                    {acHeads.map(h => <option key={h.acheadid} value={h.acheadid}>{h.acheadname} [{h.acheadcode}]</option>)}
                  </select>
                  {errMsg(errors.accountheadid)}
                  <p className="text-[11px] text-gray-400 mt-1">Income head credited when revenue is earned</p>
                </div>
                <div>
                  <label className={labelCls}>Cost Account Head <span className="text-red-500">*</span></label>
                  <select value={formData.costaccountheadid || ''} disabled={acHeadsLoading}
                    onChange={e => setFormData({ ...formData, costaccountheadid: e.target.value ? parseInt(e.target.value) : '' })}
                    className={selectCls(errors.costaccountheadid, acHeadsLoading)}>
                    <option value="">{acHeadsLoading ? 'Loading...' : '— Select Cost Account Head —'}</option>
                    {acHeads.map(h => <option key={h.acheadid} value={h.acheadid}>{h.acheadname} [{h.acheadcode}]</option>)}
                  </select>
                  {errMsg(errors.costaccountheadid)}
                  <p className="text-[11px] text-gray-400 mt-1">Expense head debited for this revenue type's cost</p>
                </div>
              </div>

              {/* Active */}
              <div className="mb-6">
                <label className="flex items-center gap-3 cursor-pointer px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all duration-150">
                  <input type="checkbox" checked={formData.isactive}
                    onChange={e => setFormData({ ...formData, isactive: e.target.checked })}
                    className="w-5 h-5 cursor-pointer accent-[#0d1c6b]" />
                  <span className="text-sm font-semibold text-gray-700">Active</span>
                </label>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button type="button" onClick={closeModal} disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed">
                  Cancel
                </button>
                <button type="submit" disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96);opacity:0 } to { transform:translateY(0) scale(1);opacity:1 } }
      `}</style>
    </div>
  );
};

export default RevenueType;